import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, XCircle, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { PageHeader, Panel } from "@/components/PageHeader";
import { api, errMsg, fdatetime, notifySettings, num } from "@/lib/api";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ImportedSource } from "@/components/sources/ImportedSource";
import { IndexPicker } from "@/components/sources/IndexPicker";

const FIELDS = [["receipts_index", "Indici scontrini (obbligatorio, separati da virgola o pattern es. ete_*_receipt)"], ["details_index", "Indici righe scontrino / dettaglio prodotti"],
  ["reservations_index", "Indici premi / prenotazioni (facoltativo, es. ete_2025_reservations,ete_2026_reservations)"], ["campaign_id", "ID campagna (vuoto = tutte)"]];
const ROLE_LABEL = { receipts_index: "Scontrini", details_index: "Righe", reservations_index: "Premi" };

export default function ApiSettings() {
  const [s, setS] = useState(null);
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState("");
  const [tab, setTab] = useState(null);
  const [err, setErr] = useState("");
  const [adv, setAdv] = useState(false);

  useEffect(() => {
    Promise.all([api.get("/settings/api"), api.get("/sources")]).then(([a, b]) => { setS(a.data); setTab(a.data.mode === "imported" ? b.data.active_type || b.data.type : "kibana"); });
    const reload = () => api.get("/settings/api").then((r) => setS(r.data));
    window.addEventListener("crm-settings-changed", reload);
    return () => window.removeEventListener("crm-settings-changed", reload);
  }, []);
  if (!s || !tab) return null;
  const set = (k) => (e) => setS({ ...s, [k]: e.target.value });

  const put = (extra) => api.put("/settings/api", { ...s, password: password || null, ...extra });
  const run = async (key, fn) => {
    setBusy(key); setErr("");
    try { await fn(); } catch (e) { const m = errMsg(e); setErr(m); toast.error(m); } finally { setBusy(""); notifySettings(); }
  };
  const missingIndex = () => {
    if (s.receipts_index?.trim()) return false;
    setErr("Indica l'indice degli scontrini nelle Impostazioni avanzate (es. sansalvatore_2025_receipt): senza indice il collegamento non può essere salvato.");
    setAdv(true);
    return true;
  };
  const connect = () => run("save", async () => {
    if (missingIndex()) return;
    await put({ mode: "external" });
    const t = await api.post("/settings/api/test");
    if (t.data.ok) toast.success(`Collegato. ${t.data.message}`); else { setErr(t.data.message); toast.error(t.data.message); }
    setS((await api.get("/settings/api")).data); setPassword("");
  });
  const test = () => run("test", async () => {
    if (missingIndex()) return;
    await put({});
    const t = await api.post("/settings/api/test");
    if (t.data.ok) toast.success(t.data.message); else { setErr(t.data.message); toast.error(t.data.message); }
    setS((await api.get("/settings/api")).data); setPassword("");
  });
  const toDemo = (clear) => run("demo", async () => {
    const r = await put({ mode: "demo", clear_credentials: clear });
    setS(r.data); setPassword("");
    toast.success(clear ? "Credenziali rimosse, modalità demo attiva" : "Modalità demo attiva");
  });
  const resetDemo = async () => {
    if (!window.confirm("Rigenerare tutti i dati di esempio?")) return;
    await api.post("/demo/reset"); toast.success("Dati demo rigenerati");
  };

  const ext = s.mode === "external";
  const addIndex = (m) => {
    const cur = (s[m.role] || "").split(",").map((x) => x.trim()).filter(Boolean);
    if (!cur.includes(m.name)) setS((p) => ({ ...p, [m.role]: [...cur, m.name].join(","), missing_indices: p.missing_indices.filter((x) => x.name !== m.name) }));
    setAdv(true);
  };
  const MODE = { external: "API Kibana", imported: "Dati importati", demo: "Dati demo" };
  return (
    <div>
      <PageHeader title="Connessione dati" subtitle="Scegli da dove il CRM legge i dati di questo circuito" />
      <div className="mb-4 text-sm text-slate-500">Fonte in uso: <span data-testid="api-current-mode-top" className={`font-medium ${s.mode === "demo" ? "text-amber-600" : "text-emerald-600"}`}>{MODE[s.mode]}</span></div>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-6 flex-wrap">
          <TabsTrigger value="kibana" data-testid="src-tab-kibana">Kibana / Elasticsearch</TabsTrigger>
          <TabsTrigger value="sql" data-testid="src-tab-sql">SQL Server</TabsTrigger>
          <TabsTrigger value="rest" data-testid="src-tab-rest">API REST</TabsTrigger>
          <TabsTrigger value="file" data-testid="src-tab-file">File CSV / Excel</TabsTrigger>
        </TabsList>
        {["sql", "rest", "file"].map((t) => <TabsContent key={t} value={t}><ImportedSource type={t} /></TabsContent>)}
        <TabsContent value="kibana">
      <div className="grid gap-6 lg:grid-cols-[1.5fr_1fr]">
        <Panel title="Servizio esterno" testId="api-settings-form">
          <div className="space-y-4 p-5">
            <div className="space-y-1.5"><Label>URL del servizio</Label><Input data-testid="api-base-url-input" placeholder="https://..." value={s.base_url} onChange={set("base_url")} /></div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1.5"><Label>Utente</Label><Input data-testid="api-username-input" value={s.username} onChange={set("username")} autoComplete="off" /></div>
              <div className="space-y-1.5"><Label>Password</Label><Input type="password" data-testid="api-password-input" autoComplete="new-password" placeholder={s.has_password ? "•••••••• (salvata)" : ""} value={password} onChange={(e) => setPassword(e.target.value)} /></div>
            </div>
            <details className="rounded-lg border border-slate-200 p-4" data-testid="api-advanced-details" open={adv || !s.receipts_index?.trim()} onToggle={(e) => setAdv(e.currentTarget.open)}>
              <summary className="cursor-pointer text-sm font-medium text-slate-700" data-testid="api-advanced-toggle">Impostazioni avanzate</summary>
              <div className="mt-4 grid gap-3 sm:grid-cols-2">
                <IndexPicker s={s} password={password} onChange={(k, v) => setS((p) => ({ ...p, [k]: v }))} />
                {FIELDS.map(([k, l]) => <div key={k} className="space-y-1"><Label className="text-xs text-slate-500">{l}</Label><Input className="font-mono text-xs" data-testid={`api-${k}-input`} value={s[k]} onChange={set(k)} /></div>)}
                <label className="flex items-center gap-3 text-sm text-slate-600 sm:col-span-2">
                  <Switch data-testid="api-verify-ssl-switch" checked={!!s.verify_ssl} onCheckedChange={(v) => setS({ ...s, verify_ssl: v })} />
                  Verifica certificato SSL (consigliata; in alternativa carica sotto il certificato CA del server)
                </label>
                <div className="space-y-1.5 sm:col-span-2" data-testid="api-ca-cert-block">
                  <Label className="text-xs text-slate-500">Certificato CA del server (PEM) {s.has_ca_cert && <span className="ml-1 rounded bg-emerald-100 px-1.5 py-0.5 text-[10px] font-medium text-emerald-700" data-testid="api-ca-cert-badge">caricato</span>}</Label>
                  <div className="flex flex-wrap items-center gap-2">
                    <input type="file" accept=".pem,.crt,.cer,.txt" data-testid="api-ca-cert-file" className="text-xs"
                      onChange={(e) => { const f = e.target.files?.[0]; if (!f) return; f.text().then((t) => setS((p) => ({ ...p, ca_cert: t }))); }} />
                    {(s.has_ca_cert || s.ca_cert) && <Button type="button" variant="outline" size="sm" data-testid="api-ca-cert-remove" onClick={() => setS((p) => ({ ...p, ca_cert: "", has_ca_cert: false }))}>Rimuovi certificato</Button>}
                  </div>
                  <p className="text-[11px] text-slate-500">Se il server usa un certificato emesso da una CA privata, caricalo qui e lascia attiva la verifica: la connessione resta protetta.</p>
                </div>
              </div>
            </details>
            {err && <div data-testid="api-error-banner" className="flex items-start gap-2 rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700">
              <XCircle className="mt-0.5 h-4 w-4 shrink-0" /><span>{err}</span></div>}
            {s.mode === "external" && !s.verify_ssl && <div data-testid="api-ssl-warning-banner" className="flex items-start gap-2 rounded-lg border-2 border-amber-400 bg-amber-50 p-3 text-sm text-amber-900">
              <XCircle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
              <span><b>Verifica del certificato SSL disattivata:</b> la connessione a Kibana/Elasticsearch non è protetta da intercettazioni. Riattiva la verifica oppure carica il certificato CA del server nelle Impostazioni avanzate.</span></div>}
            {s.missing_indices?.length > 0 && <div data-testid="api-missing-indices-banner" className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
              <div className="font-medium">Nuovi indici disponibili su Kibana non ancora inclusi</div>
              <ul className="mt-2 space-y-1">{s.missing_indices.map((m) => <li key={m.name} className="flex flex-wrap items-center gap-2">
                <span className="font-mono text-xs">{m.name}</span><span className="text-xs text-amber-700">({ROLE_LABEL[m.role]} · {num(m.documents)} documenti)</span>
                <Button size="sm" variant="outline" className="h-6 border-amber-300 px-2 text-xs" data-testid={`api-add-index-${m.name}`} onClick={() => addIndex(m)}>Aggiungi</Button></li>)}</ul>
              <p className="mt-2 text-xs">Dopo aver aggiunto gli indici premi "Salva e usa API esterne".</p>
            </div>}
            <div className="flex flex-wrap gap-2 pt-2">
              <Button variant="outline" data-testid="api-test-button" onClick={test} disabled={!!busy}>{busy === "test" ? "Verifica..." : "Test connessione"}</Button>
              <Button data-testid="api-connect-button" onClick={connect} disabled={!!busy} className="bg-blue-600 hover:bg-blue-700">{busy === "save" ? "Collegamento..." : "Salva e usa API esterne"}</Button>
              {s.mode !== "demo" && <Button variant="ghost" data-testid="api-demo-button" onClick={() => toDemo(false)} disabled={!!busy}>Torna ai dati demo</Button>}
              {s.has_password && <Button variant="ghost" className="text-rose-600 hover:text-rose-700" data-testid="api-clear-credentials-button" onClick={() => window.confirm("Rimuovere utente e password salvati?") && toDemo(true)} disabled={!!busy}>Rimuovi credenziali</Button>}
            </div>
          </div>
        </Panel>
        <div className="space-y-6">
          <Panel title="Stato" testId="api-status-panel">
            <div className="space-y-3 p-5 text-sm">
              <div className="flex items-center justify-between"><span className="text-slate-500">Sorgente dati</span>
                <span data-testid="api-current-mode" className={`font-medium ${ext ? "text-emerald-600" : "text-amber-600"}`}>{MODE[s.mode]}</span></div>
              <div className="flex items-center justify-between"><span className="text-slate-500">Ultimo test</span>
                <span className="flex items-center gap-1.5" data-testid="api-last-test">
                  {s.last_test_ok === true && <CheckCircle2 className="h-4 w-4 text-emerald-500" />}
                  {s.last_test_ok === false && <XCircle className="h-4 w-4 text-rose-500" />}
                  {s.last_test_at ? fdatetime(s.last_test_at) : "mai"}
                </span></div>
              {s.last_test_message && <p className="rounded-lg bg-slate-50 p-3 text-xs text-slate-600" data-testid="api-last-test-message">{s.last_test_message}</p>}
            </div>
          </Panel>
          <Panel title="Come funziona" testId="api-format-panel">
            <div className="space-y-2 p-5 text-xs leading-relaxed text-slate-600">
              <p>Il pannello legge i dati dal servizio Kibana con utente e password (sola lettura): scontrini, clienti fedeltà e premi riscattati.</p>
              <p>Ogni circuito ha i propri indici: indica in "Impostazioni avanzate" i nomi esatti visibili in Kibana → Index Management (es. <span className="font-mono">sansalvatore_2025_receipt</span> e <span className="font-mono">sansalvatore_2025_details</span>). L'indice premi è facoltativo: se il circuito non lo ha, lascialo vuoto.</p>
              <p>Saldo punti = punti assegnati sugli scontrini − punti dei premi riscattati + rettifiche manuali fatte dal CRM.</p>
              <p>Le rettifiche punti e i segmenti vengono salvati nel CRM: non modificano i dati del servizio esterno. I totali per cliente si aggiornano ogni 15 minuti.</p>
            </div>
          </Panel>
          {s.mode === "demo" && <Button variant="outline" className="w-full" data-testid="reset-demo-button" onClick={resetDemo}><RefreshCw className="mr-2 h-4 w-4" />Rigenera dati demo</Button>}
        </div>
      </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
